/* Created by Interactive 63 - https://interactive63.com */
/**
 * Project files: the `.tweener` wrapper format (JSON inside) for backup and sharing. Handles building
 * and parsing the wrapper, safe file names, ID reassignment on open, and saving through the File
 * System Access API where it exists, otherwise a download link.
 */
import { PROJECT_FILE_EXTENSION } from '../../core/branding';
import { createId } from '../../core/model/ids';
import type { Project } from '../../core/model/types';
import { readProjectRecord, type ProjectRecordResult, type Sanitiser } from './project-record';

export const FILE_FORMAT = 'svg-tempo-project';
export const FILE_EXTENSION = PROJECT_FILE_EXTENSION;

/** Whether a file name has the project file extension. */
export function isProjectFileName(name: string): boolean {
  return name.toLowerCase().endsWith(PROJECT_FILE_EXTENSION);
}

const UNSAFE_FILE_CHARS = /[\\/:*?"<>|]/g;

/** Replaces characters Windows rejects in file names with hyphens. */
export function safeFileName(name: string): string {
  const cleaned = name.replace(UNSAFE_FILE_CHARS, '-').trim();
  return `${cleaned === '' ? 'Untitled' : cleaned}${FILE_EXTENSION}`;
}

/** Serialises a project into the wrapper format with two-space indentation. */
export function serialiseProjectFile(project: Project): string {
  return JSON.stringify({ format: FILE_FORMAT, formatVersion: 1, project }, null, 2);
}

/** Parses and validates the text of a project file. */
export function parseProjectFile(text: string, sanitise?: Sanitiser): ProjectRecordResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, errors: ["The file isn't valid JSON."] };
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return { ok: false, errors: ["The file isn't an SVG Tweener project."] };
  }
  const wrapper = data as Record<string, unknown>;
  if (wrapper['format'] !== FILE_FORMAT) {
    return { ok: false, errors: ["The file isn't an SVG Tweener project."] };
  }
  if (wrapper['formatVersion'] !== 1) {
    return { ok: false, errors: ['This project file comes from a newer version of SVG Tweener.'] };
  }
  return readProjectRecord(wrapper['project'], sanitise);
}

/**
 * Gives an opened project a fresh ID when one with the same ID already exists locally, so
 * opening a file never silently overwrites a local project.
 */
export function withOpenedId(project: Project, idExists: boolean): Project {
  return idExists ? { ...project, id: createId('proj') } : project;
}

/** The slice of the File System Access API used for saving. */
interface FileHandle {
  createWritable: () => Promise<{
    write: (data: string) => Promise<void>;
    close: () => Promise<void>;
  }>;
}
type SaveFilePicker = (options: {
  suggestedName: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<FileHandle>;

let handle: { projectId: string; file: FileHandle } | undefined;

/** Whether the browser can save to a chosen file and rewrite it later. */
function pickerAvailable(): boolean {
  return 'showSaveFilePicker' in window;
}

/** Downloads text as a file through a temporary link. */
function download(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Saves a project file. With the File System Access API the first save asks for a location and
 * later saves of the same project write to it silently; elsewhere a download is started.
 * Returns false if the person cancelled the picker.
 */
export async function saveProjectFile(
  project: Project,
  options?: { forcePicker?: boolean },
): Promise<boolean> {
  const text = serialiseProjectFile(project);
  const fileName = safeFileName(project.name);
  if (!pickerAvailable()) {
    download(fileName, text);
    return true;
  }
  try {
    if (!handle || handle.projectId !== project.id || options?.forcePicker) {
      const picker = (window as unknown as { showSaveFilePicker: SaveFilePicker })
        .showSaveFilePicker;
      const file = await picker({
        suggestedName: fileName,
        types: [
          { description: 'SVG Tweener project', accept: { 'application/json': [FILE_EXTENSION] } },
        ],
      });
      handle = { projectId: project.id, file };
    }
    const writable = await handle.file.createWritable();
    await writable.write(text);
    await writable.close();
    return true;
  } catch (error) {
    handle = undefined;
    if (error instanceof DOMException && error.name === 'AbortError') return false;
    download(fileName, text);
    return true;
  }
}

/** Starts a plain download of a project file, for projects that are not open. */
export function downloadProjectFile(project: Project): void {
  download(safeFileName(project.name), serialiseProjectFile(project));
}
